const STORAGE_KEY = 'tasks_checklist_order'

// Only "Today" and "This week" are reorderable — they're the two chips tied
// to a rolling time window, so a manually-set position naturally goes stale
// once that window has rolled past. "Overdue"/"Someday"/"All" have no
// natural reset point, so they're left to the server's default (due date)
// order.
const TTL_MS = {
  today: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
}

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
  } catch {
    return {}
  }
}

function writeAll(all) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
}

// Per-task expiry (not one TTL for the whole stored list) so a task that
// drops out of "Today" and later reappears — rescheduled back to today a
// week later, say — doesn't silently snap back to a position it held in a
// completely different day's list.
function pruneScope(entries) {
  const now = Date.now()
  const kept = {}
  Object.entries(entries || {}).forEach(([taskId, entry]) => {
    if (entry?.expiresAt > now) kept[taskId] = entry
  })
  return kept
}

// Local-only manual ordering for the checklist's "Today"/"This week" chips.
// Deliberately never touches the backend — `position` on Task is the
// Kanban board's column ordering; this is a separate, purely-client
// preference that's fine to lose (e.g. on another device) since it's just a
// personal scratch order for a short-lived list.
export function useChecklistOrder() {
  function loadPositions(scope) {
    const all = readAll()
    const pruned = pruneScope(all[scope])
    // Persist the pruned version back so expired entries don't linger
    // forever bloating the stored blob.
    if (JSON.stringify(pruned) !== JSON.stringify(all[scope] || {})) {
      all[scope] = pruned
      writeAll(all)
    }
    return pruned
  }

  // Reorders `items` (already scope-filtered, default due-date order from
  // the server) so any task with a stored, still-valid position uses it;
  // tasks with no stored position keep their default relative order,
  // appended after the explicitly-positioned ones (new/rescheduled tasks
  // land at the bottom until dragged, same as a fresh Trello card).
  function applyOrder(scope, items) {
    if (!TTL_MS[scope]) return items
    const positions = loadPositions(scope)
    if (!Object.keys(positions).length) return items

    const ranked = []
    const unranked = []
    items.forEach((item) => {
      if (positions[item.id]) ranked.push(item)
      else unranked.push(item)
    })
    ranked.sort((a, b) => positions[a.id].position - positions[b.id].position)
    return [...ranked, ...unranked]
  }

  // Captures the exact order of `orderedIds` as the new custom order for
  // `scope`, all stamped with the same fresh expiry — a full re-save on
  // every drag (not an incremental single-item patch), so the whole visible
  // order — including tasks that had no previous custom position — is what
  // gets remembered.
  function saveOrder(scope, orderedIds) {
    if (!TTL_MS[scope]) return
    const all = readAll()
    const expiresAt = Date.now() + TTL_MS[scope]
    const entries = {}
    orderedIds.forEach((id, position) => { entries[id] = { position, expiresAt } })
    all[scope] = entries
    writeAll(all)
  }

  function isReorderable(scope) {
    return Boolean(TTL_MS[scope])
  }

  return { applyOrder, saveOrder, isReorderable }
}
